<template>
  <div
    v-if="template && (presentationMode || allBoardsMode)"
    :class="[
      'board-preview',
      { 'board-preview--presentation-view': presentationMode },
      { 'board-preview--boards-view': allBoardsMode }
    ]"
  >
    <div class="board-preview__header">
      <div
        v-if="divisionLogo"
        class="board-preview__division-logo"
        :style="`backgroundImage: url('${divisionLogo}')`"
      />

      <ElTag
        v-if="presentationMode && board.label"
        class="board-preview__label"
        effect="plain"
      >
        {{ board.label }}
      </ElTag>

      <h3
        v-if="allBoardsMode"
        class="board-preview__title"
      >
        {{ board.name }}
      </h3>
      <h2
        v-else-if="presentationMode"
        class="board-preview__title"
      >
        {{ board.name }}
      </h2>
    </div>
    <div :class="['template__container', `template__container--${templateId}`]">
      <div
        v-for="style in template.items"
        :key="style.index"
        :class="[
          'board-preview__template',
          {
            'board-preview__template--click-enabled': getStyleByIndex(
              style.index
            )
          },
          'template',
          `template--${style.index}`
        ]"
      >
        <div
          class="board-preview__style-preview-container"
          @click="
            presentationMode && getStyleByIndex(style.index)
              ? openStyleSlider(style.index)
              : null
          "
        >
          <StylePreview
            v-if="styleIdsByIndex[style.index]"
            rounded
            :no-text="allBoardsMode ? true : false"
            :style-id="styleIdsByIndex[style.index]"
            :image-resolution="imageResolution"
          />
        </div>
      </div>

      <StitchDialog
        :visible="showStyleSlider"
        fullscreen
        :modal="false"
        :show-close="false"
      >
        <TransitionGroup
          v-if="showStyleSlider && styleSlides"
          tag="div"
          class="board-preview__slider"
        >
          <div
            v-for="style in styleSlides"
            :key="style.id"
            class="board-preview__slide"
          >
            <StyleDetailsNew
              :style-id="style.id"
              :group-id="selectedGroupId"
              :show-slider-controls="hasMultipleStyles"
              @click-navigation-left="showPreviousSlide"
              @click-navigation-right="showNextSlide"
              @close="closeStyleSlider"
            />
          </div>
        </TransitionGroup>
      </StitchDialog>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { Workspace } from '@/mixins/workspace'
import { Projects } from '@/mixins/projects'
import { S3Trim } from '@/mixins/s3Trim'

import { templates } from '@/resources/templates/board.json'
import { imageResolutions } from '@/constants/image'

import StylePreview from '@/components/project/StylePreview.vue'
import StyleDetailsNew from '@/components/project/StyleDetailsNew.vue'

export default {
  name: 'BoardPreview',

  components: {
    StylePreview,
    StyleDetailsNew
  },

  mixins: [Workspace, Projects, S3Trim],

  props: {
    templateId: {
      required: true,
      type: String,
      default: '0'
    },
    boardId: {
      required: true,
      type: Number
    },
    presentationMode: Boolean,
    allBoardsMode: Boolean,
    imageResolution: {
      type: String,
      default: imageResolutions.STYLE_THUMBNAIL
    }
  },

  data: () => ({
    projectId: null,
    template: null,
    styleSlides: null,
    showStyleSlider: false
  }),

  computed: {
    ...mapGetters(['getBoardById', 'getStylesByBoardId']),

    /**
     * @returns {object}
     */
    board () {
      return this.getBoardById(this.boardId)
    },

    /**
     * @returns {object}
     */
    styles () {
      return this.getStylesByBoardId(this.boardId)
    },

    /**
     * @returns {boolean}
     */
    hasMultipleStyles () {
      return this.styles.length > 1
    },

    /**
     * @returns {object}
     */
    styleIdsByIndex () {
      const styleIdsByIndex = {}

      this.styles.forEach(style => {
        styleIdsByIndex[style.index] = style.id
      })

      return styleIdsByIndex
    },

    /**
     * @returns {string | null}
     */
    divisionLogo () {
      return this.project
        ? this.s3Trim(this.project.group.assets_logo, null, false)
        : null
    }
  },

  watch: {
    /**
     */
    templateId () {
      this.fetchTemplate(this.templateId)
    }
  },

  created () {
    this.projectId = parseInt(this.$route.params.id)
    this.fetchTemplate(this.templateId)
  },

  methods: {
    /**
     * @param   {string}        itemIndex
     *
     * @returns {object | null}
     */
    getStyleByIndex (itemIndex) {
      return this.styles.find(anItem => {
        return parseInt(anItem.index) === parseInt(itemIndex)
      })
    },

    /**
     * @param {string} templateId
     */
    fetchTemplate (templateId) {
      this.template = templates.find(template => template.id === templateId)
    },

    /**
     * @param {number} styleIndex
     */
    openStyleSlider (styleIndex) {
      const stylesBeforeSelected = this.styles.filter(
        value => value.index >= styleIndex
      )
      const restOfStyles = this.styles.filter(value => value.index < styleIndex)

      this.styleSlides = [...stylesBeforeSelected, ...restOfStyles]

      this.showStyleSlider = true
    },

    /**
     */
    closeStyleSlider () {
      this.styleSlides = null
      this.showStyleSlider = false
    },

    /**
     */
    showNextSlide () {
      const style = this.styleSlides.shift()

      this.styleSlides = this.styleSlides.concat(style)
    },

    /**
     */
    showPreviousSlide () {
      const style = this.styleSlides.pop()

      this.styleSlides = [style].concat(this.styleSlides)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/board-template';

$grid-gap: $gutter;

.board-preview {
  @include size(100%);

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .board-preview__header {
    width: 100%;
  }
}

.board-preview__division-logo {
  height: $spacer-unit-x2;
  margin-bottom: $spacer-unit;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;
}

.board-preview__title {
  @include text-ellipsis;

  width: 100%;
  padding-bottom: $spacer-unit;
  color: $dark-blue;
  font-weight: $font-weight-medium;
  text-align: center;
  text-transform: uppercase;
}

.board-preview__label {
  @include text-ellipsis;

  position: absolute;
  top: 0;
  right: 0;
  width: $width-board-label;
  padding: spacing(0.5);
  color: $black;
  font-weight: $font-weight-medium;
  text-align: center;
  text-transform: uppercase;
  border: $border-board-label;
  border-radius: $border-radius-default;
}

.board-preview--presentation-view {
  .board-preview__division-logo {
    height: $spacer-unit-x2;
    margin-bottom: $spacer-unit-x4;
  }

  .board-preview__title {
    margin-bottom: $spacer-unit-x2;
  }

  .board-preview__label {
    padding: 0 $spacer-unit;
  }
}

.board-preview--boards-view {
  .board-preview__division-logo {
    height: $spacer-unit;
    margin-bottom: $spacer-unit;
  }

  .board-preview__title {
    margin-bottom: $spacer-unit;
    padding: 0;
    font-size: $font-size-medium;
  }

  .template__container {
    cursor: pointer;

    .template {
      border: $border-grey;
    }
  }
}

.board-preview__template {
  @include size(100%);

  position: relative;
  display: flex;
  border: $border-grey;
  border-radius: $border-radius-default;
}

.board-preview__template--click-enabled {
  cursor: pointer;
}

.board-preview__style-preview-container {
  width: 100%;
}

.board-preview__style-name {
  color: $grey-blue;
  text-transform: uppercase;
}

.board-preview__slider {
  display: flex;
  width: 100%;
  overflow: hidden;
}

.board-preview__slide {
  flex: 0 0 100%;
  max-width: 100%;
}
</style>
